import tippy, { Instance } from 'tippy.js'
import { Hook, makeHook } from './Hook'

/**
 * A generic overflow tooltip hook that shows tooltips when content overflows.
 *
 * Data attributes:
 * - data-overflow-content: The text content being displayed (optional, defaults to element's text)
 * - data-overflow-tooltip: The tooltip to show when content overflows
 * - data-overflow-tooltip-when-fit: Optional tooltip to show when content fits
 * - data-overflow-selector: Optional CSS selector to find the overflow element (defaults to 'span.truncate')
 * - data-tippy-placement: Tooltip placement (top, bottom, left, right)
 */
class OverflowTooltipHook extends Hook {
  tippyInstance?: Instance
  resizeObserver?: ResizeObserver
  overflowElement?: HTMLElement

  mounted() {
    this.initializeTooltip()
    this.setupResizeObserver()
  }

  updated() {
    this.updateTooltip()
  }

  destroyed() {
    this.cleanup()
  }

  private initializeTooltip() {
    // Find the element that might overflow using custom selector or default
    const selector = this.el.dataset.overflowSelector || 'span.truncate'
    this.overflowElement = selector === 'self' ? this.el : (this.el.querySelector(selector) as HTMLElement)

    if (!this.overflowElement) {
      return
    }

    // Create tippy instance
    this.tippyInstance = tippy(this.el, {
      content: '',
      placement: (this.el.dataset.tippyPlacement as any) || 'top',
    })

    // Start disabled - we'll enable only if overflow detected
    this.tippyInstance.disable()

    // Check overflow status immediately
    this.checkOverflow()
  }

  private setupResizeObserver() {
    if (!this.overflowElement) return

    // Use ResizeObserver to detect when the element size changes
    this.resizeObserver = new ResizeObserver(() => {
      this.checkOverflow()
    })

    // Observe the container element for size changes
    this.resizeObserver.observe(this.el)

    // Also observe the overflow element if it's different
    if (this.overflowElement !== this.el) {
      this.resizeObserver.observe(this.overflowElement)
    }
  }

  private checkOverflow() {
    if (!this.overflowElement || !this.tippyInstance) return

    // Check if content is overflowing
    const isOverflowing = this.overflowElement.scrollWidth > this.overflowElement.offsetWidth

    // Get tooltip configuration
    const overflowTooltip = this.el.dataset.overflowTooltip || ''
    const tooltipWhenFit = this.el.dataset.overflowTooltipWhenFit || ''

    // Determine which tooltip to show
    const tooltipContent = isOverflowing ? overflowTooltip : tooltipWhenFit

    // Update tooltip content and visibility
    if (tooltipContent) {
      this.tippyInstance.setContent(tooltipContent)
      this.tippyInstance.enable()
    } else {
      this.tippyInstance.disable()
    }
  }

  private updateTooltip() {
    if (!this.tippyInstance) {
      this.initializeTooltip()
      return
    }

    // Check overflow again after update (this will handle content updates)
    this.checkOverflow()
  }

  private cleanup() {
    if (this.tippyInstance) {
      this.tippyInstance.destroy()
      this.tippyInstance = undefined
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = undefined
    }
  }
}

export default makeHook(OverflowTooltipHook)
